/**
 * Octopus IoT Platform - Geofence Map Component
 * FASE 2: Reusable component to render GeoJSON geofences and waypoints on Leaflet map
 *
 * IMPORTANT: GeoJSON uses [longitude, latitude], Leaflet uses [latitude, longitude]
 * Coordinates must be inverted when rendering.
 */
import React, { useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Polygon, Popup, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { cn } from '@/lib/utils';

const createWaypointIcon = (color = '#FF6B35') => L.divIcon({
  className: 'geofence-waypoint-marker',
  html: `
    <div style="
      width: 18px;
      height: 18px;
      border-radius: 9999px;
      border: 2px solid ${color};
      background: rgba(2, 6, 23, 0.9);
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.12);
    "></div>
  `,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  popupAnchor: [0, -10],
});

const getPolygonStyle = (item, selected = false) => {
  const fillEnabled = item.fill_enabled !== false;
  const fillOpacity = Number.isFinite(Number(item.fill_opacity))
    ? Number(item.fill_opacity)
    : 0.3;
  return {
    color: selected ? '#fff' : (item.border_color || item.color || '#FF6B35'),
    fillColor: item.color || '#FF6B35',
    fillOpacity: fillEnabled ? fillOpacity : 0,
    weight: selected ? Math.max(3, Number(item.border_width || 2)) : Number(item.border_width || 2),
    dashArray: item.enabled ? null : '5, 10'
  };
};

/**
 * Helper component to fit map bounds to all geometries.
 */
const FitBounds = ({ geofences }) => {
  const map = useMap();

  useEffect(() => {
    if (!geofences || geofences.length === 0) return;

    const allCoords = [];
    geofences.forEach(gf => {
      const geometryType = gf.geometry_type || (gf.point ? 'waypoint' : 'polygon');

      if (geometryType === 'polygon' && gf.polygon?.coordinates?.[0]) {
        gf.polygon.coordinates[0].forEach(([lon, lat]) => {
          allCoords.push([lat, lon]);
        });
      }

      if (geometryType === 'waypoint' && Array.isArray(gf.point?.coordinates) && gf.point.coordinates.length >= 2) {
        const [lon, lat] = gf.point.coordinates;
        allCoords.push([lat, lon]);
      }
    });

    if (allCoords.length > 0) {
      map.fitBounds(allCoords, { padding: [20, 20] });
    }
  }, [geofences, map]);

  return null;
};

/**
 * GeofenceMap Component
 *
 * @param {Array} geofences - Array of GeofenceResponse objects
 * @param {string} selectedGeofenceId - Optional ID to highlight
 * @param {string} height - Map height (default: "400px")
 * @param {boolean} interactive - Allow click on geometries (default: true)
 * @param {function} onGeofenceClick - Callback when geometry is clicked
 * @param {string} className - Additional CSS classes
 */
const GeofenceMap = ({
  geofences = [],
  selectedGeofenceId = null,
  height = '400px',
  interactive = true,
  onGeofenceClick = null,
  className = ''
}) => {
  const defaultCenter = [-23.95, -46.31];
  const defaultZoom = 10;

  const mapData = useMemo(() => {
    return geofences
      .map(gf => {
        const geometryType = gf.geometry_type || (gf.point ? 'waypoint' : 'polygon');
        const base = {
          id: gf.id,
          name: gf.name,
          description: gf.description,
          color: gf.color || '#FF6B35',
          border_color: gf.border_color || gf.color || '#FF6B35',
          border_width: gf.border_width || 2,
          fill_enabled: gf.fill_enabled !== false,
          fill_opacity: gf.fill_opacity,
          enabled: gf.enabled,
          geometryType,
          isSelected: gf.id === selectedGeofenceId,
        };

        if (geometryType === 'polygon' && gf.polygon?.coordinates?.[0]) {
          const positions = gf.polygon.coordinates[0].map(([lon, lat]) => [lat, lon]);
          return { ...base, positions };
        }

        if (geometryType === 'waypoint' && Array.isArray(gf.point?.coordinates) && gf.point.coordinates.length >= 2) {
          const [lon, lat] = gf.point.coordinates;
          return { ...base, position: [lat, lon] };
        }

        return null;
      })
      .filter(Boolean);
  }, [geofences, selectedGeofenceId]);

  const handleGeometryClick = (geometry) => {
    if (interactive && onGeofenceClick) {
      onGeofenceClick(geometry);
    }
  };

  return (
    <div className={cn('rounded-lg overflow-hidden border border-border', className)} style={{ height }}>
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
        />

        {geofences.length > 0 && <FitBounds geofences={geofences} />}

        {mapData.map((item) => {
          if (item.geometryType === 'waypoint' && item.position) {
            return (
              <Marker
                key={item.id}
                position={item.position}
                icon={createWaypointIcon(item.color)}
                eventHandlers={{ click: () => handleGeometryClick(item) }}
              >
                {interactive && (
                  <Popup>
                    <div className="p-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="font-semibold text-sm">{item.name}</span>
                      </div>
                      {item.description && (
                        <p className="text-xs text-gray-600 mb-1">{item.description}</p>
                      )}
                      <span className={cn(
                        'text-xs px-1.5 py-0.5 rounded',
                        item.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      )}>
                        {item.enabled ? 'Waypoint ativo' : 'Waypoint inativo'}
                      </span>
                    </div>
                  </Popup>
                )}
              </Marker>
            );
          }

          return (
            <Polygon
              key={item.id}
              positions={item.positions}
              pathOptions={{
                ...getPolygonStyle(item, item.isSelected)
              }}
              eventHandlers={{ click: () => handleGeometryClick(item) }}
            >
              {interactive && (
                <Popup>
                  <div className="p-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="font-semibold text-sm">{item.name}</span>
                    </div>
                    {item.description && (
                      <p className="text-xs text-gray-600 mb-1">{item.description}</p>
                    )}
                    <span className={cn(
                      'text-xs px-1.5 py-0.5 rounded',
                      item.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    )}>
                      {item.enabled ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>
                </Popup>
              )}
            </Polygon>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default GeofenceMap;
